import React, { useState, useCallback } from 'react';

import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import EntityName from 'components/Globals/EntityName';
import { usePaywallContext } from 'components/Paywall';

import { TP, CASTING_TOOL_SLUGS } from 'constants/index';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import usePageContext from 'utils/hooks/usePageContext';
import { castCrewDates } from 'utils/productions';

import { RenderEntityImage } from '../renderers/ProductionComponents';

import classes from './CreditInfoItem.module.scss';

const getWorkName = work => work.name || work.slug;

const WorkRole = ({
  title,
  showWorks,
  productionWorks,
  productionWorksCount,
  t,
  handleSelectRoleFromCreditInfoItem,
  isArchived,
}) => {
  const { setIsPaywallExpanded } = usePaywallContext();
  const [showAll, setShowAll] = useState(false);
  const isAllWorks = productionWorksCount > 1 && productionWorksCount === productionWorks?.length;
  return (
    <Typography
      variant="h3"
      className={classes.creditTitleContainer}
      onClick={handleSelectRoleFromCreditInfoItem}
      blur={isArchived}
      {...(isArchived && {
        onClick: e => {
          e.stopPropagation();
          e.preventDefault();
          setIsPaywallExpanded(true);
        },
      })}
    >
      {title}
      {showWorks && isAllWorks && (
        <Typography variant="p" size={11} color="secondary" className={classes.showAllWorks} italic>
          {t(`${TP}.FN_ALL_PROGRAM`)}
        </Typography>
      )}
      {showWorks && productionWorks?.length > 0 && !isAllWorks && (
        <>
          <Typography variant="p" size={11} color="secondary" className={classes.showAllWorks} italic>
            <ol className={classes.workList}>
              {showAll ? productionWorks?.map(work => <li>{getWorkName(work)}</li>) : getWorkName(productionWorks[0])}
            </ol>
          </Typography>
          {productionWorks.length > 1 && (
            <Typography
              weight="medium"
              variant="p"
              size={11}
              color="primary"
              onClick={() => setShowAll(!showAll)}
              italic
              underline
              className={showAll ? classes.showLess : classes.showMore}
            >
              {showAll
                ? t(`${TP}.FN_SEE_LESS_TITLE`)
                : t(`${TP}.FN_PLUS_X_MORE_NO_BRACKET`, { number: productionWorks.length - 1 })}
            </Typography>
          )}
        </>
      )}
    </Typography>
  );
};

const CreditInfoItem = ({
  title,
  data,
  suffixText,
  isArchived,
  showDetailedView = false,
  showDates = false,
  showWorks = false,
  productionWorksCount,
  physicalPerformancesCount,
  quickViewContextId,
  selectedPerformanceId,
  trackingData,
  t,
  handleRoleSelect,
  workRoleId,
  professionId,
  onCloseQuickView,
}) => {
  const { setIsPaywallExpanded } = usePaywallContext();
  const { navigate } = usePageContext();
  const { isMobile } = useDeviceTypeLayouts();
  const [expandedItems, setExpandedItems] = useState({});

  const openPaywall = () => {
    if (data?.[0]?.entity?.id === -1) {
      setIsPaywallExpanded(true);
    }
  };

  const toggleExpand = useCallback((id, e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleSelectRoleFromCreditInfoItem = () => {
    const id = professionId || workRoleId;

    if (typeof handleRoleSelect === 'function' && id) {
      const type = professionId ? CASTING_TOOL_SLUGS.PROFESSION : CASTING_TOOL_SLUGS.WORK_ROLE;
      handleRoleSelect({ id, type, works: data?.[0]?.productionWorks });
      onCloseQuickView();
    }
  };

  return (
    <div className={classnames(classes.creditInfo, { [classes.isDetailedView]: showDetailedView })}>
      <WorkRole
        title={title}
        showWorks={showWorks}
        productionWorks={data?.[0]?.productionWorks}
        productionWorksCount={productionWorksCount}
        t={t}
        handleSelectRoleFromCreditInfoItem={handleSelectRoleFromCreditInfoItem}
        isArchived={isArchived}
      />
      <ul className={classes.creditValues}>
        {data
          .filter(credit => !isEmpty(credit))
          .map((credit, index) => {
            const { isLinkable } = credit;
            const isSelected = credit.entity?.id === quickViewContextId;
            const linkProps = isLinkable
              ? navigate.getLinkProps({ entity: credit.entity, entityType: credit.entityType })
              : {};
            const entityNameNode = (
              <EntityName entityType={credit.entityType} entity={credit.entity} isRaw trackingData={trackingData} />
            );
            const dates =
              showDates &&
              (physicalPerformancesCount === credit.performances?.length ? null : castCrewDates(credit.performances));

            const isExpanded = expandedItems[index];

            return (
              <li
                key={index}
                className={classnames(classes.creditValue, {
                  [classes.isLinkable]: isLinkable,
                  [classes.isSelected]: isSelected,
                  [classes.isDisabled]: selectedPerformanceId && !credit.isPartOfPerformance,
                })}
              >
                {showDetailedView && <RenderEntityImage entity={credit} styles={{ root: classes.entityImage }} />}
                <LinkButton
                  styles={{ root: classnames(classes.creditValueLink, { [classes.isArchived]: isArchived }) }}
                  variant="native"
                  {...linkProps}
                  target="_blank"
                  isLink
                  trackingData={{
                    ...trackingData,
                    entityId: credit.entity?.id,
                    entityName: credit.entity?.name,
                    entityType: credit.entityType,
                  }}
                  preventDefault={isArchived}
                >
                  <>
                    <Typography
                      variant="p"
                      className={classnames(classes.creditValueText, {
                        [classes.creditValueTextUnderlined]: !!isSelected,
                      })}
                      color={isLinkable ? 'link' : 'primary'}
                      blur={isArchived}
                      onClick={isArchived ? openPaywall : noop}
                      weight={isSelected ? 'bold' : 'regular'}
                    >
                      <span className={classes.creditValueTextContainer}>
                        {isSelected && (
                          <SpriteIcon icon="arrow_top_left" className={classes.arrowTopLeftIcon} size={14} />
                        )}
                        {suffixText ? (
                          <span>
                            {entityNameNode}
                            {suffixText}
                          </span>
                        ) : (
                          entityNameNode
                        )}
                        {credit.isCover && <span> ({t(`${TP}.FN_COVER`)})</span>}
                      </span>
                      {linkProps.href && (
                        <SpriteIcon icon="open_in_new" size={isMobile ? 14 : 16} className={classes.openInNewTabIcon} />
                      )}
                    </Typography>
                    {showDates && dates?.length > 0 && (
                      <div className={classes.datesContainer}>
                        <div
                          className={classnames(classes.datesContainer__datesGroup, {
                            [classes.expandedDates]: isExpanded,
                          })}
                        >
                          {dates?.map((group, indexValue) => (
                            <div
                              key={`${group.groupKey}-${indexValue}`}
                              className={classes.datesContainer__datesGroup_item}
                            >
                              <Typography size={12} weight="medium" blur={isArchived}>
                                {group.month} ’{group.year}
                              </Typography>
                              <Typography size={12}>: </Typography>
                              {group.days.map((item, i) => (
                                <Typography
                                  size={12}
                                  key={`${item.original}-${i}`}
                                  weight={item.isPast ? 'regular' : 'medium'}
                                  italic
                                  blur={isArchived}
                                >
                                  {item.day}
                                  {i < group.days.length - 1 ? ', ' : ''}
                                </Typography>
                              ))}
                            </div>
                          ))}
                        </div>
                        <SpriteIcon
                          icon={isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                          size={18}
                          className={classes.expandIcon}
                          onClick={e => toggleExpand(index, e)}
                        />
                      </div>
                    )}
                  </>
                </LinkButton>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

export default CreditInfoItem;
